import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { GoAlert } from "react-icons/go";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import SelectFileInput from "../../../components/UI/SelectFileInput/SelectFileInput";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import ListaOcorrencias from "./ListaOcorrencias";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";

const validationSchema = Yup.object().shape({
  sequenceModelo: Yup.string().default(""),
  descricaoModelo: Yup.string()
    .ensure()
    .default("")
    .required("Informe o Nome do Modelo")
    .max(200, "A descrição não pode ter mais de 200 caracteres"),
  arquivo: Yup.array()
    .default([])
    .test(
      "integrity-anexos",
      "Verifique os arquivos em que constam erros. Remova-os e tente a seleção novamente. ",
      (value) => {
        for (let i = 0; i < (value || []).length; i += 1) if (value[i].error || value[i].uploading) return false;
        return true;
      },
    )
    .nullable(),
  listaOcorrenciasModImp: Yup.array().default([]),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "arquivo" || key === "listaOcorrenciasModImp") {
    initialValues[key] = [];
  } else {
    initialValues[key] = "";
  }
});

const FormModeloImportacao = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [toggledClearRows, setToggleClearRows] = useState(false);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sequenceModelo) submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraModeloImportacao, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiModeloImportacao, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid, errors } = formik;

  useEffect(() => {
    if ((submitData || submitErrors) && isSubmitting && !submitIsLoading) {
      setSubmitting(false);
    }

    if (submitErrors && isValid && !submitIsLoading) {
      setErrors({ ...formik.errors, ...submitErrors });
    }
  }, [formik.errors, isSubmitting, isValid, setErrors, setSubmitting, submitData, submitErrors, submitIsLoading]);

  useEffect(() => {
    if (errors && isSubmitting) {
      if (errors.descricaoModelo) {
        toast.warning(errors.descricaoModelo);
      }
      if (errors.arquivo) {
        toast.warning(errors.arquivo);
      }
    }
  }, [errors, isSubmitting]);

  useEffect(() => {
    if (!submitData && !submitErrors) return;

    if (submitData) {
      toast.success("Modelo de importação salvo com sucesso");
      history.push("/app/modeloimportacao");
    }
  }, [submitData, submitErrors, history]);

  // Carregar item
  const {
    request: modeloImportacaoRequest,
    data: modeloImportacaoData,
    errors: modeloImportacaoErrors,
    isLoading: modeloImportacaoIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      const params = { id: state.itemKey };
      modeloImportacaoRequest(apiRoutes.fiscalizacao.parametrizacao.consultaModeloImportacao, params);
      delete state.itemKey;
    }
  }, [modeloImportacaoRequest, state, setSubmitting]);

  useEffect(() => {
    if ((!modeloImportacaoData && !modeloImportacaoErrors) || modeloImportacaoIsLoading) return;

    if (!formik.values.sequenceModelo && modeloImportacaoData && modeloImportacaoData.result.length) {
      const info = {
        codigo: modeloImportacaoData?.result[0]?.sequenceModelo,
        data: {
          nomeFormalInclusao: modeloImportacaoData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: modeloImportacaoData?.result[0]?.nomeFormalAlteracao,
          documentoInclusao: modeloImportacaoData?.result[0]?.usuarioInclusao,
          documentoAlteracao: modeloImportacaoData?.result[0]?.usuarioAlteracao,
          dataInclusao: modeloImportacaoData?.result[0]?.dataInclusao,
          dataAlteracao: modeloImportacaoData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);

      formik.setValues({ ...modeloImportacaoData.result[0], arquivo: [] });
    }

    if (modeloImportacaoErrors) {
      toast.error(modeloImportacaoErrors.message);
    }
  }, [formik, modeloImportacaoData, modeloImportacaoErrors, modeloImportacaoIsLoading]);

  const handleClear = () => {
    formik.handleReset();
    setToggleClearRows(!toggledClearRows);
  };

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Modelos de Importação</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sequenceModelo ? "Alteração de Modelo de Importação" : "Novo Modelo de Importação"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sequenceModelo" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={submitData}
                errors={submitErrors}
                isLoading={submitIsLoading || modeloImportacaoIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Tabs defaultActiveKey="geral" transition={false} id="tabModImp">
                  <Tab
                    eventKey="geral"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.descricaoModelo} /> Dados Gerais
                      </span>
                    }
                  >
                    <fieldset
                      disabled={
                        formik.values.sequenceModelo
                          ? !isAllowed(funcionalidades.modeloImportacao.alterar)
                          : !isAllowed(funcionalidades.modeloImportacao.inserir)
                      }
                    >
                      <Row>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Nome do modelo</Form.Label>
                          <TextInputFormik formik={formik} field="descricaoModelo" maxLength="200" />
                        </Form.Group>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Arquivo</Form.Label>
                          <br />
                          <span className="mr-4">{formik.values.nomeArquivo}</span>
                          <SelectFileInput
                            id="modelos_importacao"
                            field="arquivo"
                            formik={formik}
                            accept=".xls, .xlsx"
                            save
                          />
                        </Form.Group>
                      </Row>
                    </fieldset>
                  </Tab>
                  <Tab
                    eventKey="ocorrencias"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.listaOcorrenciasModImp} /> Ocorrências
                      </span>
                    }
                    className="mb-4"
                  >
                    <ListaOcorrencias
                      formik={formik}
                      field="listaOcorrenciasModImp"
                      nomeModelo={formik.values.descricaoModelo}
                      sequencialModelo={formik.values.sequenceModelo}
                      ocorrenciasRelacionadas={formik.values.ocorrenciasModeloImportacao}
                      toggledClearRows={toggledClearRows}
                    />
                  </Tab>
                </Tabs>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Modelo de Importação..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sequenceModelo
                            ? funcionalidades.modeloImportacao.alterar
                            : funcionalidades.modeloImportacao.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sequenceModelo && (
                      <Button variant="light" className="mr-3" onClick={handleClear}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormModeloImportacao;
